import type { ValidEndpoints } from '@/api/clients/backend-client'
import type { DirectusCompany } from '@/types/directus-types'
import type { Converter } from './converter-helpers'
import { buildFetchHeaders, buildUrl, fetchCollection, updateItem } from './helper-functions'

type Fleet_expected_API_DTO = {
  id: string
  display_name: string
  logo_url?: string | null
  allow_vehicle_booking: boolean
  trip_planning_defaults?: Record<string, unknown> | null
  created_at?: Date
  created_by?: string
}

const fleetConverter: Converter<Fleet_expected_API_DTO, DirectusCompany> = {
  apiToLegacy(api) {
    return {
      allow_private_charger_booking: null,
      allow_vehicle_booking: api.allow_vehicle_booking,
      Logo: api.logo_url ?? null,
      ManualVehicleMatching: false,
      Name: api.display_name,
      third_party_vehicle_booking_only: null,
      third_party_vehicle_booking_url: null,
      id: api.id,
    }
  },
  legacyToApi(legacy) {
    return {
      allow_vehicle_booking: !!legacy.allow_vehicle_booking,
      id: legacy.id,
      display_name: legacy.Name ?? '',
      logo_url: legacy.Logo,
      trip_planning_defaults: null, //TODO implement fleet wide settings
    }
  },
}

const collection: ValidEndpoints = 'fleets'

/** API Call to fetch the company details from the DB.
 *
 * @returns a user `CompanyData` object if successful. Undefined if not.
 */
export async function fetchFleetData(): Promise<DirectusCompany | undefined> {
  const res = await fetchCollection<Fleet_expected_API_DTO, DirectusCompany>(
    collection,
    undefined,
    fleetConverter.apiToLegacy,
  )
  return res?.[0]
}

/**
 * Updates a fleets data in the DB
 * @param id db id for the fleet
 * @param dataToUpdate partial DirectusCompany object
 * @returns an updated DirectusCompany object
 */
export async function updateFleetData(
  id: string,
  dataToUpdate: Partial<Omit<DirectusCompany, 'id'>>,
): Promise<DirectusCompany | undefined> {
  const res = await updateItem<DirectusCompany>(
    collection,
    id,
    dataToUpdate,
    fleetConverter.apiToLegacy,
  )
  return res
}

/**
 * Creates a new fleet in the DB and assigns the user from the token to that fleet.
 * @body expected to at least have {'display_name': "[fleets display name]"} in the body
 * @returns a fleet object if successfully created
 *
 * NOTE: user from token is updated with the new fleet as their fleet ID
 */
export async function create_fleet(
  display_name: string,
): Promise<Fleet_expected_API_DTO | undefined> {
  // guard against invalid display name input, as this is required for fleet creation and will error if invalid.

  if (typeof display_name !== 'string') {
    throw new Error('display_name must be a string')
  }

  if (display_name.trim().length === 0) {
    throw new Error('display_name cannot be empty')
  }

  const endpoint: ValidEndpoints = 'rpc/create_fleet'
  try {
    const response = await fetch(buildUrl(endpoint), {
      method: 'POST',
      headers: buildFetchHeaders(),
      body: JSON.stringify({
        display_name: display_name,
      }),
    })
    const data = await response.json()
    return data
  } catch (error) {
    console.error(error)
    return
  }
}
